/**
 * Reusable preparatory standards page — Sprint 0143.
 *
 * Used by 4 thin route components:
 *   BreakerDFSettingsPage    → kind='breaker-df'
 *   FinisherDFSettingsPage   → kind='finisher-df'
 *   LapFormerSettingsPage    → kind='lap-former'
 *   ComberSettingsPage       → kind='comber'
 *
 * Each route gets its own URL + sidebar entry (per Option B principle),
 * but the table/form/drawer code is shared here to prevent field drift
 * and keep the operator experience identical across all 4 standards.
 *
 * Mirrors the CardingSettingsPage pattern (Sprint 0073 / 0140).
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, App, Button, Card, Col, DatePicker, Divider, Drawer, Form, Input,
  InputNumber, Modal, Row, Select, Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd'
import { DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import {
  KIND_META, preparatoryApi, type PreparatoryKind, type PrepStandardBase,
} from '@/features/production/preparatoryApi'

const { Title, Text, Paragraph } = Typography

interface MachineLite { id: string; machine_code: string; machine_name: string }
interface CountLite { id: string; count_code: string; count_ne: number; description?: string }

interface Props {
  kind: PreparatoryKind
}

export default function PreparatoryStandardsPage({ kind }: Props) {
  const meta = KIND_META[kind]
  const isComber = kind === 'comber'
  const isLapFormer = kind === 'lap-former'

  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [machineFilter, setMachineFilter] = useState<string | undefined>()
  const [activeOnly, setActiveOnly] = useState(true)
  // Sprint 0144 follow-up — match the Carding page's Edit modes.
  // supersedeMode is only relevant when editingId is set.
  const [supersedeMode, setSupersedeMode] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [form] = Form.useForm()

  // ─── Data ────────────────────────────────────────────────
  const listQ = useQuery({
    enabled: !!plantId,
    queryKey: ['prep-settings', kind, plantId, machineFilter, activeOnly],
    queryFn: () => preparatoryApi.list(kind, {
      plant_id: plantId!,
      machine_id: machineFilter,
      active_only: activeOnly,
    }),
  })

  // Machines filtered to this dept's machine_type
  // Per CLAUDE.md: machines API uses legacy aliases (section/name/machine_id_str)
  const machinesQ = useQuery({
    enabled: !!plantId,
    queryKey: ['machines-for-prep', kind, plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 500 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw
        .filter((m) =>
          (m.machine_type ?? m.section ?? '').toUpperCase() === meta.machineType
        )
        .map((m): MachineLite => ({
          id: m.id,
          machine_code: m.machine_code ?? m.machine_id_str ?? '',
          machine_name: m.machine_name ?? m.name ?? '',
        }))
    },
  })

  const countsQ = useQuery({
    queryKey: ['counts-for-prep'],
    queryFn: async () => {
      const r = await apiClient.get('/masters/counts', {
        params: { is_active: true, size: 500 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((c): CountLite => ({
        id: c.id,
        count_code: c.count_code ?? c.description ?? `${c.count_ne ?? '?'}s`,
        count_ne: Number(c.count_ne ?? 0),
        description: c.description,
      }))
    },
  })

  const machineMap = useMemo(() => {
    const m = new Map<string, MachineLite>()
    for (const x of machinesQ.data ?? []) m.set(x.id, x)
    return m
  }, [machinesQ.data])

  const machineOptions = (machinesQ.data ?? []).map((m) => ({
    label: `${m.machine_code} — ${m.machine_name}`,
    value: m.id,
  }))
  const countOptions = (countsQ.data ?? []).map((c) => {
    const ne = c.count_ne ? `${c.count_ne}s` : ''
    const desc = c.description && c.description !== c.count_code ? c.description : ''
    const label = [ne, desc].filter(Boolean).join(' — ') || c.count_code || c.id.slice(0, 6)
    return { label, value: c.id }
  })

  // ─── Mutations ───────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: (body: any) => preparatoryApi.create(kind, body),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['prep-settings', kind] })
      setCreateOpen(false)
      setEditingId(null)
      form.resetFields()
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Standard change sent for Plant Head approval')
      } else {
        message.success('Standard saved')
      }
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      preparatoryApi.update(kind, id, body),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['prep-settings', kind] })
      setCreateOpen(false)
      setEditingId(null)
      form.resetFields()
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Standard change sent for Plant Head approval')
      } else {
        message.success('Standard updated')
      }
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Update failed'),
  })

  const deactivateMut = useMutation({
    mutationFn: (id: string) => preparatoryApi.deactivate(kind, id),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['prep-settings', kind] })
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Deactivation sent for Plant Head approval')
      } else {
        message.success('Standard deactivated')
      }
    },
  })

  // ─── Master-data preflight banner ────────────────────────
  const noMachines = machinesQ.isSuccess && (machinesQ.data?.length ?? 0) === 0
  const noCounts = countsQ.isSuccess && (countsQ.data?.length ?? 0) === 0

  // ─── Table columns ───────────────────────────────────────
  const baseColumns = [
    {
      title: 'Machine',
      dataIndex: 'machine_id',
      key: 'machine_id',
      width: 130,
      render: (v: string | null) =>
        v ? (machineMap.get(v)?.machine_code ?? v.slice(0, 6)) : <Tag>Any</Tag>,
    },
    {
      title: 'Count',
      dataIndex: 'count_id',
      key: 'count_id',
      width: 110,
      render: (v: string | null) => {
        if (!v) return <Tag>Any</Tag>
        const opt = countOptions.find((o) => o.value === v)
        return opt?.label ?? v.slice(0, 6)
      },
    },
    { title: 'Deliveries', dataIndex: 'num_deliveries', key: 'nd', width: 80, align: 'right' as const },
    {
      title: 'Speed m/min', dataIndex: 'delivery_speed_m_min', key: 'sp', width: 100,
      align: 'right' as const,
      render: (v: number) => v?.toFixed(0) ?? '—',
    },
    {
      title: 'Hank', dataIndex: 'output_hank', key: 'hk', width: 80,
      align: 'right' as const,
      render: (v: number) => v?.toFixed(4) ?? '—',
    },
    {
      title: 'Eff %', dataIndex: 'std_efficiency_pct', key: 'eff', width: 70,
      align: 'right' as const,
    },
    {
      title: 'Waste %', dataIndex: 'std_waste_pct', key: 'waste', width: 80,
      align: 'right' as const,
    },
    {
      title: 'Can kg', dataIndex: 'std_can_kg', key: 'can', width: 80,
      align: 'right' as const,
    },
  ]

  const comberColumns = isComber ? [
    {
      title: 'Noil %', dataIndex: 'std_noil_pct', key: 'noil', width: 80,
      align: 'right' as const,
    },
    {
      title: 'Real %', dataIndex: 'std_realization_pct', key: 'real', width: 80,
      align: 'right' as const,
    },
    {
      title: 'Recon tol %', dataIndex: 'reconciliation_tolerance_pct', key: 'tol', width: 100,
      align: 'right' as const,
    },
  ] : []

  const lapColumns = isLapFormer ? [
    {
      title: 'Lap wt kg', dataIndex: 'std_lap_weight_kg', key: 'lw', width: 100,
      align: 'right' as const,
      render: (v: number | null) => v?.toFixed(2) ?? '—',
    },
    {
      title: 'Lap m', dataIndex: 'std_lap_length_m', key: 'll', width: 80,
      align: 'right' as const,
      render: (v: number | null) => v?.toFixed(0) ?? '—',
    },
    {
      title: 'Lap ktex', dataIndex: 'lap_ktex', key: 'lk', width: 80,
      align: 'right' as const,
      render: (v: number | null) => v?.toFixed(2) ?? '—',
    },
  ] : []

  const tailColumns = [
    { title: 'From', dataIndex: 'effective_from', key: 'ef', width: 100,
      render: (v: string) => dayjs(v).format('DD MMM YY') },
    { title: 'To', dataIndex: 'effective_to', key: 'et', width: 100,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YY') : '—' },
    {
      title: 'Status', dataIndex: 'is_active', key: 'is_active', width: 90,
      render: (active: boolean) => active
        ? <Tag color="green">Active</Tag>
        : <Tag>Inactive</Tag>,
    },
    // Sprint 0144 follow-up — audit trail column. Tooltip shows the
    // short user-id so reports can answer "who last touched this row".
    {
      title: 'Updated', dataIndex: 'updated_at', key: 'updated_at',
      width: 130,
      render: (v: string | null, r: any) => {
        if (!v) return <Text type="secondary">—</Text>
        return (
          <Tooltip title={r.updated_by ? `by ${String(r.updated_by).slice(0, 8)}…` : 'system'}>
            <Text style={{ fontSize: 12 }}>
              {dayjs(v).format('DD MMM YY HH:mm')}
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: '', key: 'action', fixed: 'right' as const, width: 150,
      render: (_: unknown, r: PrepStandardBase) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          {r.is_active && (
            <Button
              size="small" danger
              onClick={() => Modal.confirm({
                title: 'Deactivate this standard?',
                onOk: () => deactivateMut.mutate(r.id),
              })}
            >Deactivate</Button>
          )}
        </Space>
      ),
    },
  ]

  const columns = [...baseColumns, ...comberColumns, ...lapColumns, ...tailColumns]

  // ─── Open create / edit drawer ───────────────────────────
  const openCreate = () => {
    setEditingId(null)
    form.resetFields()
    form.setFieldsValue({
      effective_from: dayjs(),
      std_efficiency_pct: 88,
      std_waste_pct: 1,
      std_can_kg: 25,
      num_deliveries: isComber ? 8 : 8,
      ...(isComber ? {
        std_noil_pct: 18,
        std_realization_pct: 82,
        reconciliation_tolerance_pct: 2,
      } : {}),
    })
    setCreateOpen(true)
  }

  const openEdit = (r: PrepStandardBase) => {
    setEditingId(r.id)
    form.setFieldsValue({
      ...r,
      effective_from: dayjs(r.effective_from),
      effective_to: r.effective_to ? dayjs(r.effective_to) : null,
      qc_measured_at: r.qc_measured_at ? dayjs(r.qc_measured_at) : null,
    })
    setCreateOpen(true)
  }

  const onSubmit = (v: any) => {
    if (!plantId) return message.error('Plant not loaded')
    const today = dayjs().format('YYYY-MM-DD')
    const payload: any = {
      ...v,
      plant_id: plantId,
      effective_from: (v.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
      effective_to: v.effective_to
        ? (v.effective_to as dayjs.Dayjs).format('YYYY-MM-DD')
        : null,
      qc_measured_at: v.qc_measured_at
        ? (v.qc_measured_at as dayjs.Dayjs).format('YYYY-MM-DD')
        : null,
    }
    if (editingId && supersedeMode) {
      // Supersede path — deactivate the row with today as effective_to,
      // then create a fresh one with today as effective_from. Keeps the
      // history timeline intact for past-date reports.
      preparatoryApi.deactivate(kind, editingId)
        .then(() => preparatoryApi.create(kind, {
          ...payload,
          effective_from: today,
        }))
        .then(() => {
          message.success('Standard superseded with new dated row')
          setCreateOpen(false); setEditingId(null); form.resetFields()
          setSupersedeMode(false)
          qc.invalidateQueries({ queryKey: ['prep-settings', kind] })
        })
        .catch((e: any) => message.error(
          e?.response?.data?.detail ?? 'Supersession failed',
        ))
    } else if (editingId) {
      updateMut.mutate({ id: editingId, body: payload })
    } else {
      createMut.mutate(payload)
    }
  }

  // Excel export — respects the same filters as the on-screen list.
  const exportHandler = async () => {
    if (!plantId) { message.error('Plant not loaded'); return }
    setExporting(true)
    try {
      await preparatoryApi.exportSettings(kind, {
        plant_id: plantId,
        machine_id: machineFilter,
        active_only: activeOnly,
      })
      message.success(`Downloaded ${kind}_settings.xlsx`)
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>{meta.label}</Title>
      <Text type="secondary">
        Per-(machine, count) standards for {meta.shortLabel} machines.
        Drives daily-shift-log auto-fetch (expected output, can size, waste %).
        Precedence: machine+count → machine → count → default.
      </Text>

      {(noMachines || noCounts) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Master data missing — set this up first"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {noMachines && (
                <li>
                  No <Text code>{meta.machineType}</Text> machines in this plant.{' '}
                  <a href="/masters/machines">Add machines →</a>
                </li>
              )}
              {noCounts && (
                <li>
                  No counts in Count Master.{' '}
                  <a href="/masters/counts">Add counts →</a>
                </li>
              )}
            </ul>
          }
        />
      )}

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Machine:</Text>
          <Select
            allowClear
            placeholder="All machines"
            style={{ width: 220 }}
            value={machineFilter}
            onChange={setMachineFilter}
            options={machineOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Tooltip title="When off, deactivated/superseded standards appear too — that's your audit trail. Each row's From/To dates show when it was in effect.">
            <span>
              <Switch
                checked={!activeOnly}
                onChange={(v) => setActiveOnly(!v)}
                size="small"
              /> Show history
            </span>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>Refresh</Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={exportHandler}
          >
            Export Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Standard
          </Button>
        </Space>
      </Card>

      <Table
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={listQ.data ?? []}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 50 }}
      />

      <Drawer
        title={editingId ? `Edit ${meta.shortLabel} Standard` : `New ${meta.shortLabel} Standard`}
        open={createOpen}
        onClose={() => {
          setCreateOpen(false); setEditingId(null)
          setSupersedeMode(false)
          form.resetFields()
        }}
        width={680}
        destroyOnClose
      >
        <Paragraph type="secondary">
          One row per (machine, count) combo. Leave machine or count blank to apply broadly.
          Effective-from versioning lets you keep history when standards change.
        </Paragraph>
        {editingId && (
          <Card
            size="small"
            style={{ marginBottom: 12, background: '#fafafa' }}
          >
            <Space size={16} wrap>
              <Space size={4}>
                <Switch
                  size="small"
                  checked={supersedeMode}
                  onChange={setSupersedeMode}
                />
                <Text strong>Supersede with new dated standard</Text>
              </Space>
              <Tooltip title="ON → deactivates this row (effective_to = today) and creates a new row with today as effective_from + your edits. Past reports stay accurate. OFF → patches this row in place — use for typo fixes only.">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {supersedeMode
                    ? 'Deactivate old + create new (preserves history)'
                    : 'Patch in place (fast, no history trace)'}
                </Text>
              </Tooltip>
            </Space>
          </Card>
        )}
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Divider orientation="left">Scope</Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="machine_id" label="Machine">
                <Select allowClear showSearch options={machineOptions}
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="(any)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="count_id" label="Count">
                <Select allowClear options={countOptions} placeholder="(any)" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="yarn_type" label="Yarn type (optional)">
            <Input placeholder="CARDED / COMBED / COMPACT" />
          </Form.Item>

          <Divider orientation="left">Formula inputs</Divider>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
            Production kg = Speed × Deliveries × 60 × Runtime hrs × Ktex / 1000 × Eff%/100.
            Ktex = 0.5905 / Hank.
          </Paragraph>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="num_deliveries"
                label="Number of Deliveries"
                rules={[{ required: true }]}
                tooltip="Number of slivers/laps produced in parallel (typical 6-8)"
              >
                <InputNumber min={1} max={32} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="delivery_speed_m_min"
                label="Delivery Speed (m/min)"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} step={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="output_hank"
                label="Output Hank Ne"
                rules={[{ required: true }]}
                tooltip="Sliver hank (Ne). Ktex computed as 0.5905 / hank."
              >
                <InputNumber min={0.001} step={0.01} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Performance targets</Divider>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="std_efficiency_pct" label="Std efficiency %">
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="std_waste_pct" label="Std waste %">
                <InputNumber min={0} max={20} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="std_can_kg" label="Std can kg">
                <InputNumber min={1} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="std_can_length_m" label="Can length m">
                <InputNumber min={0} step={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {isLapFormer && (
            <>
              <Divider orientation="left">Lap Former extras</Divider>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="std_lap_weight_kg" label="Std lap weight kg">
                    <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="std_lap_length_m" label="Std lap length m">
                    <InputNumber min={0} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="lap_ktex"
                    label="Lap ktex"
                    tooltip="Lap weight = lap_length × lap_ktex / 1000"
                  >
                    <InputNumber min={0} step={1} precision={4} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {isComber && (
            <>
              <Divider orientation="left">Comber extras</Divider>
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item
                    name="std_noil_pct"
                    label="Std noil %"
                    tooltip="Industry typical 14-22% for combed cotton. Drives noil expectation per shift."
                  >
                    <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="std_realization_pct"
                    label="Std realization %"
                    tooltip="Output / input × 100. Typical 78-86%."
                  >
                    <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="input_lap_ktex"
                    label="Input lap ktex"
                    tooltip="Expected ktex of the lap coming from Lap Former."
                  >
                    <InputNumber min={0} step={1} precision={4} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="reconciliation_tolerance_pct"
                    label="Recon tolerance %"
                    tooltip="If |input − (output + noil + waste)| / input > this %, system raises an exception for GM review."
                  >
                    <InputNumber min={0} max={20} step={0.5} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Divider orientation="left">QC measurement (per spec)</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="qc_measured_at"
                label="Last QC on"
                tooltip="Date QC last weighed a sample at this machine. Stale (>60 days) gets flagged."
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="qc_sample_kg" label="Sample kg">
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="e.g. 50" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Versioning</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="effective_to" label="Effective to">
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Tooltip title="Active standards are used by Daily Shift Log auto-fetch">
            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          </Tooltip>

          <Button
            type="primary"
            htmlType="submit"
            loading={createMut.isPending || updateMut.isPending}
            block
          >
            {editingId
              ? (supersedeMode ? 'Save as new dated standard' : 'Save changes')
              : 'Create standard'}
          </Button>
        </Form>
      </Drawer>
    </div>
  )
}
